import { useCallback, useEffect, useMemo } from 'react';

import { createRealtimeServiceFromAngularWebSocket } from '../../../services/Utilities';
import type {
  AcademyConfirmedBy,
  AcademyLesson,
  TeacherAcademyDashboard,
} from './AcademyService';
import AcademyRealtimeService from './AcademyRealtimeService';

interface RealtimeEventDetail {
  detail?: {
    record?: unknown;
  };
}

export interface AcademySkillFinishedMessage {
  skillKey: string;
  learnConfirmedBy: AcademyConfirmedBy;
  applyConfirmedBy: AcademyConfirmedBy;
}

interface AcademyRealtimeOptions {
  onSkillFinished: (message: AcademySkillFinishedMessage) => void;
}

const ACADEMY_CONFIRMED_BY_VALUES = new Set<AcademyConfirmedBy>(['none', 'self', 'system']);

export function useAcademyRealtime({ onSkillFinished }: AcademyRealtimeOptions): void {
  const academyRealtimeService = useMemo(() => {
    return createRealtimeServiceFromAngularWebSocket<AcademyRealtimeService>(AcademyRealtimeService);
  }, []);

  const handleSkillFinished = useCallback((event: Event) => {
    const message = parseAcademySkillFinishedMessage(
      (event as Event & RealtimeEventDetail).detail?.record,
    );

    if (!message) {
      return;
    }

    onSkillFinished(message);
  }, [onSkillFinished]);

  useEffect(() => {
    if (!academyRealtimeService) {
      return;
    }

    academyRealtimeService.on(
      AcademyRealtimeService.EVENTS.SkillFinished,
      handleSkillFinished,
    );

    return () => {
      academyRealtimeService.removeListener(
        AcademyRealtimeService.EVENTS.SkillFinished,
        handleSkillFinished,
      );
    };
  }, [academyRealtimeService, handleSkillFinished]);
}

export function applyAcademySkillFinishedUpdate(
  dashboard: TeacherAcademyDashboard,
  message: AcademySkillFinishedMessage,
): TeacherAcademyDashboard {
  let didUpdate = false;

  const academyAreas = dashboard.academyAreas.map((area) => {
    let didUpdateArea = false;
    const recommendedLessons = area.recommendedLessons.map((lesson) => {
      const updatedLesson = applySkillFinishedToLesson(lesson, message);

      if (updatedLesson !== lesson) {
        didUpdateArea = true;
      }

      return updatedLesson;
    });
    const skillPaths = area.skillPaths.map((skillPath) => {
      let didUpdatePath = false;
      const skills = skillPath.skills.map((skill) => {
        const updatedSkill = applySkillFinishedToLesson(skill, message);

        if (updatedSkill !== skill) {
          didUpdatePath = true;
        }

        return updatedSkill;
      });

      if (!didUpdatePath) {
        return skillPath;
      }

      didUpdateArea = true;
      return {
        ...skillPath,
        skills,
      };
    });

    if (!didUpdateArea) {
      return area;
    }

    didUpdate = true;
    return {
      ...area,
      recommendedLessons,
      skillPaths,
    };
  });

  if (!didUpdate) {
    return dashboard;
  }

  return {
    ...dashboard,
    academyAreas,
  };
}

export function parseAcademySkillFinishedMessage(value: unknown): AcademySkillFinishedMessage | null {
  if (!isObjectRecord(value)) {
    return null;
  }

  const {
    skillKey,
    learnConfirmedBy,
    applyConfirmedBy,
  } = value;

  if (
    typeof skillKey !== 'string' ||
    !isAcademyConfirmedBy(learnConfirmedBy) ||
    !isAcademyConfirmedBy(applyConfirmedBy)
  ) {
    return null;
  }

  return {
    skillKey,
    learnConfirmedBy,
    applyConfirmedBy,
  };
}

function applySkillFinishedToLesson(
  lesson: AcademyLesson,
  message: AcademySkillFinishedMessage,
): AcademyLesson {
  if (lesson.skillKey !== message.skillKey) {
    return lesson;
  }

  if (
    lesson.learnConfirmedBy === message.learnConfirmedBy &&
    lesson.applyConfirmedBy === message.applyConfirmedBy
  ) {
    return lesson;
  }

  return {
    ...lesson,
    learnConfirmedBy: message.learnConfirmedBy,
    applyConfirmedBy: message.applyConfirmedBy,
  };
}

function isAcademyConfirmedBy(value: unknown): value is AcademyConfirmedBy {
  return typeof value === 'string' && ACADEMY_CONFIRMED_BY_VALUES.has(value as AcademyConfirmedBy);
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export default useAcademyRealtime;
